import type { DocumentType } from '@/types/verification';

/**
 * Which compliance registration a Fix-it trade requires.
 *
 * ## Why this lives here and not in the signup form
 *
 * `TRADE_COMPLIANCE` was declared inside `FixItSquadForm.tsx`, which meant the
 * verification centre could not see it. The two models of the same fact drifted:
 * the signup form asked for registration numbers, and the verification centre
 * asked for a single generic "trade certificate" that had no document type, no
 * status field and no upload control anywhere in the codebase.
 *
 * The rules themselves were always right, and they match the South African
 * position: regulated trades must evidence their registration, and general or
 * handyman work does not. Trades absent from this map are deliberately
 * unmapped — a locksmith, glazier, painter or gardener needs no statutory
 * registration to trade.
 *
 * ## What is deliberately not mapped
 *
 * `cidb` is left out. A CIDB grading is required to tender for public works, not
 * to offer services to a homeowner through the platform, and mapping it would
 * block sole operators who have never registered. `public_liability_insurance`
 * is a voluntary Enterprise-tier badge, not an onboarding gate.
 */

/**
 * Version of the criminal-record declaration wording an owner actually agreed to.
 *
 * The warranty is the platform's position for the MVP, so the exact text a
 * person accepted has to be recoverable later. Bump this whenever the wording
 * changes materially; it is stored alongside the acceptance timestamp.
 */
export const CRIMINAL_RECORD_WARRANTY_VERSION = '2026-10-15.v1';

/** Compliance bodies, keyed by the identifier the signup form has always used. */
export type TradeComplianceBody = 'doel' | 'ecb' | 'pirb' | 'nhbrc' | 'psira' | 'gas' | 'cidb';

export const TRADE_COMPLIANCE: Record<string, TradeComplianceBody[]> = {
  electrician: ['doel', 'ecb'],
  plumber: ['pirb'],
  'new-build': ['nhbrc', 'cidb'],
  alterations: ['nhbrc', 'cidb'],
  'demolition-earthworks': ['cidb'],
  'gate-garage-fence-automation': ['psira'],
  'cctv-alarm': ['psira'],
  'gas-installer': ['gas'],
};

/**
 * The document each compliance body is evidenced by.
 *
 * `doel` and `ecb` both resolve to one document because the position is "a
 * Wireman's licence or registered electrical contractor registration", not both.
 */
export const COMPLIANCE_DOCUMENT_BY_BODY: Partial<Record<TradeComplianceBody, DocumentType>> = {
  doel: 'ELECTRICAL_CONTRACTOR_REGISTRATION',
  ecb: 'ELECTRICAL_CONTRACTOR_REGISTRATION',
  pirb: 'PIRB_REGISTRATION',
  nhbrc: 'NHBRC_CERTIFICATE',
  psira: 'REPSSA_REGISTRATION',
  gas: 'SAQCC_GAS_PRACTITIONER',
};

/** Owner-facing name for the certification, used in the upload field and toast. */
export const CERTIFICATION_LABEL_BY_DOCUMENT: Partial<Record<DocumentType, string>> = {
  ELECTRICAL_CONTRACTOR_REGISTRATION: "Electrical contractor registration",
  PIRB_REGISTRATION: "PIRB registration",
  NHBRC_CERTIFICATE: "NHBRC certificate",
  REPSSA_REGISTRATION: "REPSSA registration",
  SAQCC_GAS_PRACTITIONER: "SAQCC gas practitioner",
};

/**
 * What the owner is told before they pick a file. Naming the document is the
 * whole point - "Your trade certificate" told a plumber to upload something they
 * do not have, and told a painter to upload something nobody needs.
 */
export const CERTIFICATION_GUIDANCE_BY_DOCUMENT: Partial<Record<DocumentType, string>> = {
  ELECTRICAL_CONTRACTOR_REGISTRATION:
    "Your Wireman's licence, or your registered electrical contractor registration from the Department of Employment and Labour or the Electrical Contractors Board. You need one of these before you may issue a Certificate of Compliance.",
  PIRB_REGISTRATION:
    "Your PIPlumbing / PIRB registration. It must show your name and registration number, and must not be expired.",
  NHBRC_CERTIFICATE:
    "Your NHBRC registration certificate. Your annual builder registration or project enrolment will both work.",
  REPSSA_REGISTRATION:
    "Your PSIRA registration certificate. Security and gate automation work is regulated, and the certificate must not be expired.",
  SAQCC_GAS_PRACTITIONER:
    "Your SAQCC Gas practitioner licence, showing your practitioner number and gas type. It must not be expired.",
};

/** Reads the trades a Fix-it profile declares, however they were stored. */
export const readDeclaredTrades = (
  profile: Partial<Record<string, unknown>> | null | undefined,
): string[] => {
  if (!profile) return [];

  const asString = (value: unknown): string =>
    typeof value === 'string' ? value.trim() : '';

  const trades = new Set<string>();
  const primary = asString(profile.fixitPrimaryTrade);
  if (primary) trades.add(primary);

  const additional = profile.fixitAdditionalTrades;
  if (Array.isArray(additional)) {
    for (const trade of additional) {
      const value = asString(trade);
      if (value) trades.add(value);
    }
  }

  return [...trades];
};

/**
 * The certifications this owner's declared trades require, de-duplicated and in
 * a stable order.
 *
 * Returns an empty array for an unregulated trade, which is correct: a painter
 * is not blocked by not having a trade certificate, because none exists.
 */
export const resolveRequiredCertifications = (
  profile: Partial<Record<string, unknown>> | null | undefined,
): DocumentType[] => {
  const required = new Set<DocumentType>();

  for (const trade of readDeclaredTrades(profile)) {
    for (const body of TRADE_COMPLIANCE[trade] ?? []) {
      const document = COMPLIANCE_DOCUMENT_BY_BODY[body];
      // `cidb` has no mapping on purpose: see the note at the top of the file.
      if (document) required.add(document);
    }
  }

  return [...required];
};

/** True when at least one of the owner's trades is statutorily regulated. */
export const isRegulatedTrade = (
  profile: Partial<Record<string, unknown>> | null | undefined,
): boolean => resolveRequiredCertifications(profile).length > 0;